/**
 * VCI-P1 Slice 4 — Destination Intelligence Preview
 * Route: /destination-preview/:slug
 *
 * Internal preview of the SEO-ready Hyderabad destination profile.
 * Phase 1: noindex, internal access only, "Internal Preview" banner.
 * Reuses canonical venue 360° data for associated venue cards.
 *
 * Features:
 *  - Destination hero section (name, tagline, suitability, season)
 *  - Travel accessibility info
 *  - Associated venue grid (linked to VenuePublicProfile)
 *  - Venue type filter
 *  - JSON-LD TouristDestination injected into document head
 *  - noindex meta injected
 *  - Internal preview amber banner
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  MapPin,
  Users,
  Sun,
  Cloud,
  Plane,
  Train,
  Star,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  Hotel,
  Filter,
  ExternalLink,
  ChevronRight,
  Info,
  Tag,
  Building2,
  Trees,
  Shield,
  Calendar,
  CheckCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge }  from "../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const VENUE_TYPE_LABELS = {
  GARDEN:       "Garden / Farmhouse",
  BANQUET_HALL: "Banquet Hall",
  HOTEL:        "Hotel / Resort",
  RESORT:       "Resort",
  ROOFTOP:      "Rooftop",
  BEACH:        "Beach",
  DESTINATION:  "Destination",
  CONFERENCE:   "Conference",
  OTHER:        "Venue",
};

const VENUE_TYPE_FILTERS = [
  { value: "",             label: "All Types" },
  { value: "GARDEN",      label: "Garden / Farmhouse" },
  { value: "BANQUET_HALL",label: "Banquet Hall" },
  { value: "HOTEL",       label: "Hotel / Resort" },
  { value: "RESORT",      label: "Resort" },
  { value: "CONFERENCE",  label: "Conference" },
];

// ── Venue card ────────────────────────────────────────────────────────────────
function VenueCard({ venue }) {
  const navigate = useNavigate();
  const geo      = venue.geo_location || {};
  const hasPrice = !!venue.pricing_indicative_from;
  const currency = venue.pricing_currency || "INR";
  const priceStr = hasPrice
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 })
        .format(venue.pricing_indicative_from)
    : null;

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
      data-testid={`vci04-venue-card-${venue.venue_slug || venue.id?.slice(-6) || "na"}`}
    >
      {/* Cover image */}
      <div className="relative h-36 bg-gray-100 overflow-hidden">
        {venue.cover_image_url ? (
          <img
            src={venue.cover_image_url}
            alt={venue.venue_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <Hotel size={32} />
          </div>
        )}
        {venue.is_published && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-emerald-500 text-white text-xs border-0 px-2 py-0.5">
              Published
            </Badge>
          </div>
        )}
        {venue.verification_status === "VERIFIED" && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-blue-500 text-white text-xs border-0 px-2 py-0.5">
              <CheckCircle size={10} className="inline mr-1" />Verified
            </Badge>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-3 space-y-2">
        <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-1">
          {venue.venue_name}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {venue.venue_type && (
            <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-100">
              {VENUE_TYPE_LABELS[venue.venue_type] || venue.venue_type}
            </Badge>
          )}
          {venue.max_capacity && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Users size={11} /> {venue.max_capacity.toLocaleString()}
            </span>
          )}
        </div>
        {geo.locality && (
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <MapPin size={11} /> {geo.locality}
          </p>
        )}
        {priceStr && (
          <p className="text-xs text-gray-500">From <strong>{priceStr}</strong></p>
        )}
      </div>

      {/* Footer CTA */}
      {venue.venue_slug && (
        <div className="px-3 pb-3">
          <button
            onClick={() => navigate(`/venue-preview/${venue.venue_slug}`)}
            className="w-full text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 py-1.5 border border-blue-100 rounded-lg hover:bg-blue-50 transition-colors"
            data-testid={`vci04-venue-preview-link-${venue.venue_slug}`}
          >
            View Profile <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Travel accessibility card ─────────────────────────────────────────────────
function TravelCard({ travel }) {
  if (!travel) return null;
  const items = [
    { icon: Plane, label: "By Air",   list: travel.airports          || [] },
    { icon: Train, label: "By Rail",  list: travel.railway_stations  || [] },
    { icon: MapPin,label: "Metro",    list: travel.metro_lines       || [] },
  ].filter(i => i.list.length > 0);

  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="vci04-travel-section">
      {items.map(({ icon: Icon, label, list }) => (
        <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-700 font-medium text-sm mb-2">
            <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
              <Icon size={14} className="text-blue-600" />
            </div>
            {label}
          </div>
          <ul className="space-y-1">
            {list.map((item, i) => (
              <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                <span className="w-1 h-1 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DestinationPreview() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [venueType, setVenueType]     = useState("");
  const [filteredVenues, setFilteredVenues] = useState([]);

  // ── SEO: noindex + structured data ────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    const ed = data.destination?.editorial || {};

    // noindex meta
    let noindexMeta = document.querySelector('meta[name="robots"][data-vci04]');
    if (!noindexMeta) {
      noindexMeta = document.createElement("meta");
      noindexMeta.setAttribute("name", "robots");
      noindexMeta.setAttribute("data-vci04", "true");
      document.head.appendChild(noindexMeta);
    }
    noindexMeta.setAttribute("content", "noindex, nofollow");

    // document title
    const prevTitle = document.title;
    document.title = `${data.destination?.name || "Destination"} — Internal Preview | Prashasta`;

    // JSON-LD
    let ldScript = document.querySelector('script[type="application/ld+json"][data-vci04]');
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.setAttribute("type", "application/ld+json");
      ldScript.setAttribute("data-vci04", "true");
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify(data.structured_data || {});

    return () => {
      noindexMeta?.remove();
      ldScript?.remove();
      document.title = prevTitle;
    };
  }, [data]);

  // ── Venue type filter ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    const venues = data.associated_venues || [];
    setFilteredVenues(venueType ? venues.filter(v => v.venue_type === venueType) : venues);
  }, [data, venueType]);

  const fetchDestination = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/venue-commerce/destinations/${slug}`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        setError({ type: "not_found", msg: `No destination found for "${slug}"` });
      } else if (status === 403) {
        setError({ type: "forbidden", msg: "You do not have permission to access this page." });
      } else {
        setError({ type: "error", msg: detail || "Failed to load destination." });
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchDestination(); }, [fetchDestination]);

  // ── States ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="vci04-loading">
        <Loader2 size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4" data-testid="vci04-error">
        <AlertCircle size={48} className="text-amber-400" />
        <h2 className="text-2xl font-bold text-gray-800">
          {error.type === "not_found" ? "Destination Not Found" : "Preview Unavailable"}
        </h2>
        <p className="text-gray-500 max-w-sm">{error.msg}</p>
        <Button variant="outline" onClick={() => navigate("/venue-commerce/catalogue")} className="mt-4">
          <ArrowLeft size={16} className="mr-2" /> Back to Catalogue
        </Button>
      </div>
    );
  }

  const dest   = data.destination || {};
  const ed     = dest.editorial   || {};
  const travel = ed.travel_accessibility || null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="vci04-destination-page">

      {/* ── Internal Preview Banner ─────────────────────────────────────── */}
      <div
        className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-4 sticky top-0 z-30"
        data-testid="vci04-internal-banner"
      >
        <div className="flex items-center gap-2 text-amber-800">
          <EyeOff size={14} className="flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider">Internal Preview</span>
          <span className="text-xs text-amber-600 hidden sm:inline">— Phase 1 gate active · Not indexed · Hyderabad Foundation</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`text-xs ${dest.is_published ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-amber-400 text-amber-700 bg-amber-50"}`}
          >
            {dest.is_published ? "Published" : "Not Published"}
          </Badge>
          <Badge variant="outline" data-testid="vci04-phase-badge" className="text-xs border-purple-300 text-purple-600 bg-purple-50">
            {dest.phase || "VCI-P1-S4"}
          </Badge>
        </div>
      </div>

      {/* ── Navigation ─────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="vci04-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 space-y-10">

        {/* ── Hero section ──────────────────────────────────────────────── */}
        <section data-testid="vci04-hero-section">
          {/* Hero image */}
          {ed.hero_image_url ? (
            <div className="rounded-2xl overflow-hidden h-64 sm:h-80 mb-6">
              <img
                src={ed.hero_image_url}
                alt={`${dest.name} destination`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 h-40 sm:h-52 mb-6 flex items-center justify-center">
              <div className="text-center text-white">
                <Building2 size={40} className="mx-auto mb-2 opacity-70" />
                <p className="text-blue-200 text-sm">No hero image yet</p>
              </div>
            </div>
          )}

          {/* Destination header */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <MapPin size={14} />
                <span>{dest.city}, {dest.country === "IN" ? "India" : dest.country}</span>
              </div>
              <h1
                className="text-4xl font-bold text-gray-900"
                data-testid="vci04-destination-name"
              >
                {dest.name}
              </h1>
              {ed.tagline && (
                <p
                  className="text-lg text-gray-500 italic"
                  data-testid="vci04-destination-tagline"
                >
                  {ed.tagline}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{data.venue_count}</p>
              <p className="text-xs text-gray-400">Associated Venues</p>
            </div>
          </div>

          {/* Suitability + season tags */}
          <div className="flex flex-wrap gap-2 mt-4">
            {(ed.event_suitability || []).map(s => (
              <Badge
                key={s}
                className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                data-testid={`vci04-suit-${s.toLowerCase()}`}
              >
                {s.replace(/_/g, " ")}
              </Badge>
            ))}
            {(ed.destination_tags || []).map(t => (
              <Badge key={t} variant="outline" className="text-xs text-gray-600 border-gray-200">
                <Tag size={11} className="mr-1" />
                {t.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </section>

        {/* ── Description + Season ─────────────────────────────────────── */}
        {(ed.description || ed.best_season) && (
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {ed.description && (
              <div className="sm:col-span-2" data-testid="vci04-description-section">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">About Hyderabad</h2>
                <p className="text-gray-600 leading-relaxed text-sm">{ed.description}</p>
              </div>
            )}
            <div className="space-y-4">
              {ed.best_season && (
                <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="vci04-season-card">
                  <div className="flex items-center gap-2 text-amber-600 font-medium text-sm mb-1">
                    <Sun size={15} /> Best Season
                  </div>
                  <p className="text-gray-800 font-semibold text-sm">{ed.best_season}</p>
                </div>
              )}
              {ed.weather_note && (
                <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="vci04-weather-card">
                  <div className="flex items-center gap-2 text-blue-500 font-medium text-sm mb-1">
                    <Cloud size={15} /> Climate
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{ed.weather_note}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Travel accessibility ─────────────────────────────────────── */}
        {travel && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Getting Here</h2>
            <TravelCard travel={travel} />
            {travel.note && (
              <div className="mt-3 flex items-start gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                {travel.note}
              </div>
            )}
          </section>
        )}

        {/* ── Associated venues ─────────────────────────────────────────── */}
        <section data-testid="vci04-venues-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Venues in {dest.name}
              <span className="text-gray-400 font-normal ml-2 text-base">({data.venue_count})</span>
            </h2>
            {/* Venue type filter */}
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-400" />
              <select
                value={venueType}
                onChange={e => setVenueType(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                data-testid="vci04-venue-type-filter"
              >
                {VENUE_TYPE_FILTERS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {filteredVenues.length === 0 ? (
            <div
              className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200 text-gray-400"
              data-testid="vci04-venues-empty"
            >
              <Hotel size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">
                {venueType ? `No ${VENUE_TYPE_LABELS[venueType] || venueType} venues found` : "No associated venues yet"}
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
              data-testid="vci04-venue-grid"
            >
              {filteredVenues.map((venue, i) => (
                <VenueCard key={venue.id || i} venue={venue} />
              ))}
            </div>
          )}
        </section>

        {/* ── SEO metadata section (internal reference) ────────────────── */}
        {(ed.seo_title || ed.seo_description) && (
          <section
            className="bg-white rounded-xl border border-gray-200 p-5"
            data-testid="vci04-seo-section"
          >
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              SEO Metadata (Internal Reference)
            </h2>
            {ed.seo_title && (
              <p className="text-sm text-gray-900 font-medium mb-1">
                Title: <span className="font-normal text-gray-600">{ed.seo_title}</span>
              </p>
            )}
            {ed.seo_description && (
              <p className="text-sm text-gray-900 font-medium">
                Description: <span className="font-normal text-gray-600">{ed.seo_description}</span>
              </p>
            )}
            {(ed.seo_keywords || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {ed.seo_keywords.map(k => (
                  <code key={k} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {k}
                  </code>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Slug reference ───────────────────────────────────────────── */}
        <div className="text-center text-xs text-gray-400">
          <code data-testid="vci04-slug-display">{dest.destination_slug}</code>
        </div>

      </main>
    </div>
  );
}
